import { apiClient } from './apiClient'

interface User {
  id?: string
  email: string
  roles?: string[]
  [key: string]: unknown
}

interface AuthResponse {
  user: User
}

interface AuthServiceData {
  user?: User
  token?: string
  [key: string]: unknown
}

export const authService = {
  async login(email: string, password: string): Promise<{ user: User }> {
    const response = await apiClient.post<AuthResponse>('/auth/login', { email, password })
    const { user } = response.data
    return { user }
  },

  async register(userData: Record<string, unknown>): Promise<{ user: User }> {
    const response = await apiClient.post<AuthResponse>('/auth/register', userData)
    const { user } = response.data
    return { user }
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      apiClient.clearAuth()
    }
  },

  async refreshToken(): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>('/auth/refresh')
    return response.data
  },

  async verifyEmail(token: string): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>('/auth/verify-email', { token },
      { suppressErrorToast: true } as Record<string, unknown>)
    return response.data
  },

  async forgotPassword(email: string): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>('/auth/forgot-password', { email })
    return response.data
  },

  async validateResetToken(token: string): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>(
      `/auth/validate-reset-token?token=${encodeURIComponent(token)}`
    )
    return response.data
  },

  async resetPassword(token: string, newPassword: string): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>('/auth/reset-password', {
      token,
      newPassword
    })
    return response.data
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>('/users/change-password', {
      currentPassword,
      newPassword
    }, { suppressErrorToast: true } as Record<string, unknown>)
    return response.data
  },

  async requestEmailChange(newEmail: string, currentPassword?: string): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>('/users/me/email-change', {
      newEmail,
      currentPassword: currentPassword || null
    }, { suppressErrorToast: true } as Record<string, unknown>)
    return response.data
  },

  async confirmEmailChange(token: string): Promise<AuthServiceData> {
    const response = await apiClient.post<AuthServiceData>('/auth/confirm-email-change', { token },
      { suppressErrorToast: true } as Record<string, unknown>)
    return response.data
  }
}

